import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useWindowSize } from 'usehooks-ts';

import profilesAPI from '@/api/profiles';
import ColorPicker from '@/components/ColorPicker';
import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import ToggleField from '@/components/ToggleField';
import UploadPhoto from '@/components/UploadPhoto';
import { COVER_IMAGE } from '@/constants/files';
import MESSAGES from '@/constants/messages-en';
import useAppState from '@/hooks/useAppState';
import useAuth from '@/hooks/useAuth';

import Button, { BUTTON_KIND } from '../Button';
import ClearProfileWarningModal from './ClearProfileWarningModal';
import DeleteProfileWarningModal from './DeleteProfileWarningModal';
import {
  ContactExchangeForm,
  ProfilePageAppearanceForm,
  WorkingProfileForm,
} from './schema';

export function ProfilePageAppearance() {
  const { watch, setValue } = useFormContext<ProfilePageAppearanceForm>();

  const settingsLevel = watch('settings_level');
  const isCustomDesignEnabled = settingsLevel === 'profile';

  const logo = watch('settings.logo');

  const colorPickers = [
    {
      title: 'Background colour',
      value: watch('settings.bg_color'),
      setter: (color: string) => setValue('settings.bg_color', color),
    },
    {
      title: 'Text colour',
      value: watch('settings.text_color'),
      setter: (color: string) => setValue('settings.text_color', color),
    },
    {
      title: 'Button colour',
      value: watch('settings.button_bg_color'),
      setter: (color: string) => setValue('settings.button_bg_color', color),
    },
    {
      title: 'Button text colour',
      value: watch('settings.button_text_color'),
      setter: (color: string) => setValue('settings.button_text_color', color),
    },
  ];

  return (
    <div className="space-y-6">
      <ToggleField
        label="Assign a custom profile page design to this profile holder"
        enabled={isCustomDesignEnabled}
        setter={enabled =>
          setValue('settings_level', enabled ? 'profile' : 'organisation')
        }
      />
      {isCustomDesignEnabled && (
        <>
          <UploadPhoto
            title="Cover image"
            photo={logo ?? undefined}
            setPhoto={logo => {
              setValue('settings.logo', logo ?? null);
              setValue('settings.logo_file_id', logo?.id ?? null);
            }}
            aspectRatio={16 / 11}
            fileFormatMessage="Recommended dimensions 1024px x 704px"
            maxHeight={COVER_IMAGE.MAX_HEIGHT}
            maxWidth={COVER_IMAGE.MAX_WIDTH}
            size="large"
          />
          <div className="grid grid-cols-2 gap-5 w-full max-w-2xl">
            {colorPickers.map((picker, index) => (
              <div key={index} className="space-y-3">
                <span className="block text-sm leading-5 font-medium text-gray-900">
                  {picker.title}
                </span>
                <ColorPicker color={picker.value} setColor={picker.setter} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ContactExchange() {
  const { watch, setValue } = useFormContext<ContactExchangeForm>();

  const isContactExchangeEnabled = watch('contact_exchange_enabled');

  return (
    <ToggleField
      label="Enable contact exchange form"
      enabled={isContactExchangeEnabled || false}
      setter={enabled => setValue('contact_exchange_enabled', enabled)}
      description="Choose whether you would like to receive contact details from people."
    />
  );
}

export function ProfileOperations() {
  const { orgID } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [, , profileID] = location.pathname.split('/');

  const { getValues } = useFormContext<WorkingProfileForm>();
  const { width } = useWindowSize();

  const [isClearProfileWarningOpen, setIsClearProfileWarningOpen] =
    useState(false);
  const [isDeleteProfileWarningOpen, setIsDeleteProfileWarningOpen] =
    useState(false);

  const { mutateAsync: clearProfile, isPending: isClearing } = useMutation({
    mutationFn: resetProfile,
  });
  const { mutateAsync: removeProfile, isPending: isDeleting } = useMutation({
    mutationFn: deleteProfile,
    onSuccess: () => {
      toast.success(
        MESSAGES.profile.delete.successWithName(
          `${getValues('first_name')} ${getValues('last_name')}`,
        ),
      );
      navigate('/');
    },
  });

  async function resetProfile() {
    if (orgID === undefined) {
      return;
    }

    const { data: profile } = await profilesAPI.resetProfile(
      orgID,
      parseInt(profileID),
    );

    navigate(`/edit-profile/${profile.data.id}`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  async function deleteProfile() {
    if (orgID === undefined) {
      return;
    }

    await profilesAPI.deleteProfile(orgID, parseInt(profileID));
  }

  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-row items-center space-x-1">
        <span className="font-medium text-gray-900 text-sm">
          Profile Actions
        </span>
        <InformationCircleIcon className="w-5 h-5 text-brand-500" />
      </div>
      <div className="flex flex-row space-x-2">
        <Button
          buttonText="Clear"
          kind={BUTTON_KIND.WHITE}
          onClick={() => setIsClearProfileWarningOpen(true)}
          loading={isClearing}
        />
        <Button
          buttonText={width < 768 ? 'Delete' : 'Delete profile'}
          kind={BUTTON_KIND.RED}
          onClick={() => setIsDeleteProfileWarningOpen(true)}
          loading={isDeleting}
        />
      </div>
      <ClearProfileWarningModal
        isOpen={isClearProfileWarningOpen}
        setIsOpen={setIsClearProfileWarningOpen}
        onSuccess={clearProfile}
        loading={isClearing}
      />
      <DeleteProfileWarningModal
        isOpen={isDeleteProfileWarningOpen}
        setIsOpen={setIsDeleteProfileWarningOpen}
        onSuccess={removeProfile}
        loading={isDeleting}
      />
    </div>
  );
}

export function ProfileManagement() {
  return (
    <InfoPanelContainer>
      <div className="text-base font-medium text-gray-900 pb-6 border-b border-gray-200">
        Profile settings &amp; management
      </div>
      <div className="space-y-8 mt-6">
        <ContactExchange />
        <ProfilePageAppearance />
        <ProfileOperations />
      </div>
    </InfoPanelContainer>
  );
}

export function ProfileManagementBulkEdit() {
  return (
    <InfoPanelContainer>
      <div className="text-base font-medium text-gray-900 pb-6 border-b border-gray-200">
        Profile settings &amp; management
      </div>
      <div className="space-y-8 mt-6">
        <ContactExchange />
        <ProfilePageAppearance />
        <ProfileOperationsBulkEdit />
      </div>
    </InfoPanelContainer>
  );
}

export function ProfileOperationsBulkEdit() {
  const { orgID } = useAuth();
  const navigate = useNavigate();
  const { multipleProfiles: profileIDs } = useAppState();

  const { width } = useWindowSize();

  const [isClearProfileWarningOpen, setIsClearProfileWarningOpen] =
    useState(false);
  const [isDeleteProfileWarningOpen, setIsDeleteProfileWarningOpen] =
    useState(false);

  const { mutateAsync: clearProfiles, isPending: isClearing } = useMutation({
    mutationFn: resetProfiles,
    onSuccess: () => {
      toast.success(`${profileIDs.length} profiles cleared successfully`);
      navigate('/');
    },
  });
  const { mutateAsync: removeProfiles, isPending: isDeleting } = useMutation({
    mutationFn: deleteProfiles,
    onSuccess: () => {
      toast.success(`${profileIDs.length} profiles deleted successfully`);
      navigate('/');
    },
  });

  async function resetProfiles() {
    if (orgID === undefined) {
      return;
    }

    await profilesAPI.resetProfiles(orgID, profileIDs);
  }

  async function deleteProfiles() {
    if (orgID === undefined) {
      return;
    }

    await profilesAPI.deleteProfiles(orgID, profileIDs);
  }

  return (
    <div className="flex flex-row justify-between">
      <div className="flex flex-row items-center space-x-1">
        <span className="font-medium text-gray-900 text-sm">
          Profile Actions
        </span>
        <InformationCircleIcon className="w-5 h-5 text-brand-500" />
      </div>
      <div className="flex flex-row space-x-2">
        <Button
          buttonText="Clear"
          kind={BUTTON_KIND.WHITE}
          onClick={() => setIsClearProfileWarningOpen(true)}
          loading={isClearing}
        />
        <Button
          buttonText={width < 768 ? 'Delete' : 'Delete profile'}
          kind={BUTTON_KIND.RED}
          onClick={() => setIsDeleteProfileWarningOpen(true)}
          loading={isDeleting}
        />
      </div>
      <ClearProfileWarningModal
        isOpen={isClearProfileWarningOpen}
        setIsOpen={setIsClearProfileWarningOpen}
        onSuccess={clearProfiles}
        loading={isClearing}
      />
      <DeleteProfileWarningModal
        isOpen={isDeleteProfileWarningOpen}
        setIsOpen={setIsDeleteProfileWarningOpen}
        onSuccess={removeProfiles}
        loading={isDeleting}
      />
    </div>
  );
}
